const ADMIN_TOKEN_KEY = "hei_admin_token";

function apiBase() {
    return String(process.env.REACT_APP_DELIVERY_API_URL || "").replace(/\/+$/, "");
}

function adminToken() {
    try {
        return localStorage.getItem(ADMIN_TOKEN_KEY) || "";
    } catch {
        return "";
    }
}

async function parseResponse(response) {
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(body?.error?.message || "REQUEST_FAILED");
        error.status = response.status;
        throw error;
    }
    return body;
}

async function adminRequest(path, { method = "GET", body } = {}) {
    const response = await fetch(`${apiBase()}${path}`, {
        method,
        headers: {
            Authorization: `Bearer ${adminToken()}`,
            ...(body ? { "Content-Type": "application/json" } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
        cache: "no-store",
    });
    return parseResponse(response);
}

function publicHeaders(token, extra = {}) {
    return { "X-Retouch-Token": token, ...extra };
}

async function publicRequest(path, token, { method = "GET", body, headers = {} } = {}) {
    const response = await fetch(`${apiBase()}${path}`, {
        method,
        headers: {
            ...publicHeaders(token, headers),
            ...(body ? { "Content-Type": "application/json" } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
        cache: "no-store",
    });
    return parseResponse(response);
}

export function getRetouchReviewApiBase() {
    return apiBase();
}

export async function createRetouchReview(input) {
    const data = await adminRequest("/api/retouch-review/create", { method: "POST", body: input });
    return data.review;
}

export async function listRetouchReviews() {
    const data = await adminRequest("/api/retouch-reviews");
    return data.reviews || [];
}

export async function getRetouchReview(id) {
    const data = await adminRequest(`/api/retouch-review/${encodeURIComponent(id)}`);
    return data.review || null;
}

export async function updateRetouchReview(id, input) {
    await adminRequest(`/api/retouch-review/${encodeURIComponent(id)}`, { method: "PUT", body: input });
}

export async function syncRetouchReview(id, { publish = false } = {}) {
    const data = await adminRequest(`/api/retouch-review/${encodeURIComponent(id)}/sync`, {
        method: "POST",
        body: { publish },
    });
    return data;
}

export async function deleteRetouchReview(id) {
    await adminRequest(`/api/retouch-review/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function fetchRetouchReviewInfo(token) {
    const data = await fetch(`${apiBase()}/api/retouch-review/info/${encodeURIComponent(token)}?_=${Date.now()}`, {
        cache: "no-store",
    }).then(parseResponse);
    return data.review || null;
}

export async function fetchRetouchComments(token, photoId) {
    const data = await publicRequest(
        `/api/retouch-review/comments/${encodeURIComponent(token)}?photoId=${encodeURIComponent(photoId)}`,
        token
    );
    return data.comments || [];
}

export async function postRetouchComment(token, input) {
    const data = await publicRequest("/api/retouch-review/comment", token, { method: "POST", body: input });
    return data.comment;
}

export async function approveRetouchPhoto(token, input) {
    const data = await publicRequest("/api/retouch-review/approve", token, { method: "POST", body: input });
    return data;
}

export async function adminPostRetouchComment(input) {
    const data = await adminRequest("/api/retouch-review/comment", { method: "POST", body: input });
    return data.comment;
}

export async function adminApproveRetouchPhoto(input) {
    const data = await adminRequest("/api/retouch-review/approve", { method: "POST", body: input });
    return data;
}

export async function adminDeleteRetouchComment(input) {
    await adminRequest("/api/retouch-review/comment", { method: "DELETE", body: input });
}

export async function createRetouchUploadBatch({ id, token, files, mode = "batch" }) {
    const body = { mode, files: files.map(({ version, relativePath, size }) => ({ version, relativePath, size })) };
    const url = `${apiBase()}/api/retouch-review/${encodeURIComponent(id)}/upload-batch`;
    const response = await fetch(url, {
        method: "POST",
        headers: token ? publicHeaders(token, { "Content-Type": "application/json" }) : { Authorization: `Bearer ${adminToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        cache: "no-store",
    });
    return parseResponse(response);
}

const RETOUCH_UPLOAD_MAX_ATTEMPTS = 3;
const RETOUCH_UPLOAD_RETRY_DELAY_MS = 500;

function delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function uploadRetouchFileOnce({ batchId, token, file, version, relativePath, onProgress }) {
    const url = `${apiBase()}/api/retouch-review/upload-batch/${encodeURIComponent(batchId)}/file`;
    const headers = token
        ? publicHeaders(token, { "Content-Type": file.type || "application/octet-stream", "X-Retouch-Version": version, "X-Retouch-Path": encodeURIComponent(relativePath), "X-Retouch-Size": String(file.size) })
        : { Authorization: `Bearer ${adminToken()}`, "Content-Type": file.type || "application/octet-stream", "X-Retouch-Version": version, "X-Retouch-Path": encodeURIComponent(relativePath), "X-Retouch-Size": String(file.size) };

    // fetch does not expose upload progress; use XHR only for the progress callback.
    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) onProgress?.(event.loaded / event.total);
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else {
                let message = `Upload failed (${xhr.status})`;
                try {
                    const body = JSON.parse(xhr.responseText || "{}");
                    message = body?.error?.message || message;
                } catch {
                    // Keep the HTTP status when the Worker did not return JSON.
                }
                const error = new Error(message);
                error.status = xhr.status;
                reject(error);
            }
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(file);
    });
}

export async function uploadRetouchFile(input) {
    let lastError = null;
    for (let attempt = 1; attempt <= RETOUCH_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
        try {
            if (attempt > 1) input.onProgress?.(0);
            await uploadRetouchFileOnce(input);
            return;
        } catch (error) {
            lastError = error;
            if (attempt < RETOUCH_UPLOAD_MAX_ATTEMPTS) {
                await delay(RETOUCH_UPLOAD_RETRY_DELAY_MS * attempt);
            }
        }
    }
    throw lastError || new Error("Upload failed");
}

export async function completeRetouchUpload({ batchId, token }) {
    if (token) {
        return publicRequest(`/api/retouch-review/upload-batch/${encodeURIComponent(batchId)}/complete`, token, { method: "POST" });
    }
    return adminRequest(`/api/retouch-review/upload-batch/${encodeURIComponent(batchId)}/complete`, { method: "POST" });
}

export async function getRetouchUploadReport({ batchId, token }) {
    if (token) {
        return publicRequest(`/api/retouch-review/upload-batch/${encodeURIComponent(batchId)}/report`, token);
    }
    return adminRequest(`/api/retouch-review/upload-batch/${encodeURIComponent(batchId)}/report`);
}

export async function syncRetouchReviewPublic({ id, token }) {
    const data = await publicRequest(`/api/retouch-review/${encodeURIComponent(id)}/sync`, token, { method: "POST", body: {} });
    return data;
}

export function getRetouchAdminToken() {
    return adminToken();
}
